/* =========================================================================
   CONSTANTES DEL DOMINIO
   Los valores salen del backend real (tablas `rol`, `orderssales.state`,
   `payments.status`), no de suposiciones. Si el backend cambia, este archivo
   es el único sitio que se toca.
   ======================================================================== */

/**
 * Tabla `rol`: 1 comprador, 2 tendero, 3 domiciliario, 4 admin,
 * 5 dueño de conjunto, 6 celador.
 *
 * Los dos últimos llegaron con el panel de aliados y faltaban acá: una cuenta
 * de conjunto salía en el listado de usuarios con la etiqueta en blanco, como
 * si el dato estuviera corrupto.
 */
export const ROLES = {
  1: { label: "Comprador", tone: "info", short: "C" },
  2: { label: "Tendero", tone: "brand", short: "T" },
  3: { label: "Domiciliario", tone: "warn", short: "D" },
  4: { label: "Administrador", tone: "bad", short: "A" },
  5: { label: "Admin. de conjunto", tone: "ok", short: "AC" },
  6: { label: "Celador", tone: "neutral", short: "CE" },
};

export const ROLE_OPTIONS = Object.entries(ROLES).map(([value, r]) => ({
  value: Number(value),
  label: r.label,
}));

export const ROL_ADMIN = 4;

/**
 * Los roles que pertenecen a UN conjunto concreto.
 *
 * No basta con crearles la cuenta: hace falta además la ficha en
 * `complex_staff` que dice cuál conjunto es el suyo, porque todo el panel de
 * aliados se acota por ella. Sin esa fila la cuenta entra y no ve nada.
 */
export const ROL_DUENO_CONJUNTO = 5;
export const ROL_CELADOR = 6;
export const ROLES_DE_CONJUNTO = [ROL_DUENO_CONJUNTO, ROL_CELADOR];

/**
 * `orderssales.state`
 * 1 en preparación · 2 listo para recoger · 3 en camino · 4 entregado
 * El 5 (cancelado) no existe hoy en la base; se deja mapeado para no romper
 * la vista si el backend empieza a emitirlo.
 */
export const ORDER_STATES = {
  1: { label: "En preparación", tone: "warn" },
  2: { label: "Listo para recoger", tone: "info" },
  3: { label: "En camino", tone: "brand" },
  4: { label: "Entregado", tone: "ok" },
  5: { label: "Cancelado", tone: "bad" },
};

export const ORDER_STATE_OPTIONS = Object.entries(ORDER_STATES).map(
  ([value, s]) => ({ value: Number(value), label: s.label }),
);

/** Estados activos = todo lo que aún no se entregó ni se canceló. */
export const ACTIVE_ORDER_STATES = [1, 2, 3];

/** `orderssales.payment_state` / `payments.status` */
export const PAYMENT_STATES = {
  approved: { label: "Aprobado", tone: "ok" },
  paid: { label: "Pagado", tone: "ok" },
  pending: { label: "Pendiente", tone: "warn" },
  pending_online: { label: "Pendiente en línea", tone: "warn" },
  rejected: { label: "Rechazado", tone: "bad" },
  failed: { label: "Fallido", tone: "bad" },
  cancelled: { label: "Cancelado", tone: "neutral" },
  refunded: { label: "Reembolsado", tone: "neutral" },
};

/** `business.state` y demás banderas de activación del esquema. */
export const RECORD_STATES = {
  1: { label: "Activo", tone: "ok" },
  0: { label: "Inactivo", tone: "neutral" },
};

/**
 * Reglas de la operación: SOLO el respaldo del primer fotograma.
 *
 * Estos valores ya NO son la referencia. Las reglas vigentes llegan del servidor
 * con los permisos (`useAuth().reglas`), porque desde que se editan en Ajustes
 * una copia en el panel se queda vieja en cuanto alguien las cambia: la pantalla
 * diría "máximo 3 entregas" mientras el servidor ya permite cinco.
 *
 * Se conservan para que el primer render —antes de que llegue la respuesta— no
 * pinte "comisión 0 %", que se leería como un dato real y equivocado. Los usa
 * `AuthContext` y nadie más.
 */
export const BUSINESS_RULES = {
  maxActiveDeliveries: 3,
  domiciliaryShare: 0.25,
};

export const PAGE_SIZES = [10, 25, 50, 100];

/* =========================================================================
   MARKETING
   Los valores son contratos con el backend (columnas enum) y con la app, que
   es quien sabe dibujar cada sitio. Cambiar una clave acá sin cambiarla allá
   deja banners que se guardan bien y no se muestran en ninguna parte.
   ======================================================================== */

export const CAMPAIGN_STATES = {
  0: { label: "Borrador", tone: "neutral" },
  1: { label: "Activa", tone: "ok" },
  2: { label: "Pausada", tone: "warn" },
  3: { label: "Finalizada", tone: "neutral" },
};

export const CAMPAIGN_STATE_OPTIONS = Object.entries(CAMPAIGN_STATES).map(
  ([value, s]) => ({ value: Number(value), label: s.label }),
);

export const CAMPAIGN_OBJECTIVES = {
  awareness: { label: "Reconocimiento", hint: "Se mide por impresiones" },
  traffic: { label: "Tráfico", hint: "Se mide por clics" },
  conversion: { label: "Conversión", hint: "Se mide por pedidos" },
};

/** Dónde se pinta cada banner. La app tiene que saber dibujar el sitio. */
export const BANNER_PLACEMENTS = {
  home_hero: {
    label: "Carrusel principal",
    hint: "Inicio de la app, tamaño grande",
  },
  home_strip: { label: "Franja del inicio", hint: "Bajo las categorías" },
  listing_inline: {
    label: "Dentro del listado",
    hint: "Intercalado entre negocios",
  },
  splash: { label: "Pantalla de entrada", hint: "Al abrir la app" },
  web_home: { label: "Cabecera web", hint: "Portada del sitio" },
};

export const BANNER_PLATFORMS = {
  app: { label: "Solo app" },
  web: { label: "Solo web" },
  both: { label: "App y web" },
};

/**
 * Qué pasa al tocar la pieza. Se separa el tipo del valor para que el cliente
 * no tenga que adivinar si "123" es un negocio o una dirección web.
 */
export const LINK_TYPES = {
  none: { label: "Sin acción", hint: "Solo se muestra" },
  url: { label: "Abrir enlace", hint: "Dirección web completa" },
  business: { label: "Ir a un negocio", hint: "Identificador del negocio" },
  product: { label: "Ir a un producto", hint: "Identificador del producto" },
  category: {
    label: "Ir a una categoría",
    hint: "Identificador de la categoría",
  },
};

export const FEATURED_PLACEMENTS = {
  home_top: { label: "Primeros del inicio" },
  category_top: { label: "Primeros de su categoría" },
  search_top: { label: "Primeros en búsqueda" },
};

export const COUPON_TYPES = {
  percent: { label: "Porcentaje", suffix: "%" },
  fixed: { label: "Monto fijo", suffix: "$" },
};

/**
 * Nivel de acceso dentro del área.
 *
 * El ÁREA dice qué secciones le tocan a alguien; el NIVEL, si además puede
 * modificarlas. Es lo que permite que un auxiliar y su jefe compartan área sin
 * tener que duplicarla ("Marketing" y "Marketing consulta"), que convertiría
 * siete áreas en catorce y obligaría a marcar cada módulo nuevo dos veces.
 */
export const ACCESS_LEVELS = {
  gestor: {
    label: "Gestor",
    tone: "ok",
    hint: "Crea, edita y borra en las secciones de su área.",
  },
  consulta: {
    label: "Solo consulta",
    tone: "neutral",
    hint: "Ve exactamente lo mismo, pero no modifica nada.",
  },
};

/* =========================================================================
   SST · CALIDAD · CONTABILIDAD
   ======================================================================== */

/** Documentos de un domiciliario. Los cinco primeros los exige la ley. */
export const DOC_TYPES = {
  licencia: { label: "Licencia de conducción", obligatorio: true },
  soat: { label: "SOAT", obligatorio: true },
  tecnomecanica: { label: "Tecnomecánica", obligatorio: true },
  arl: { label: "Afiliación ARL", obligatorio: true },
  eps: { label: "Afiliación EPS", obligatorio: true },
  examen_medico: { label: "Examen médico ocupacional", obligatorio: false },
  cedula: { label: "Cédula", obligatorio: false },
  otro: { label: "Otro", obligatorio: false },
};

/**
 * Situación de vigencia. La calcula el servidor y llega en `situation`:
 * calcularla acá también significaría dos fuentes que pueden discrepar.
 */
export const DOC_SITUATIONS = {
  vencido: { label: "Vencido", tone: "bad" },
  por_vencer: { label: "Por vencer", tone: "warn" },
  vigente: { label: "Vigente", tone: "ok" },
  sin_vencimiento: { label: "Sin vencimiento", tone: "neutral" },
  archivado: { label: "Archivado", tone: "neutral" },
};

export const INCIDENT_TYPES = {
  accidente_transito: { label: "Accidente de tránsito" },
  caida: { label: "Caída" },
  robo: { label: "Robo" },
  agresion: { label: "Agresión" },
  falla_vehiculo: { label: "Falla del vehículo" },
  condicion_insegura: {
    label: "Condición insegura",
    hint: "Riesgo detectado que aún no causó daño",
  },
  otro: { label: "Otro" },
};

export const INCIDENT_SEVERITIES = {
  leve: { label: "Leve", tone: "neutral" },
  moderado: { label: "Moderado", tone: "warn" },
  grave: { label: "Grave", tone: "bad" },
};

export const INCIDENT_STATES = {
  0: { label: "Abierto", tone: "warn" },
  1: { label: "En investigación", tone: "info" },
  2: { label: "Cerrado", tone: "ok" },
};

export const PQRS_TYPES = {
  peticion: { label: "Petición" },
  queja: { label: "Queja" },
  reclamo: { label: "Reclamo" },
  sugerencia: { label: "Sugerencia" },
  felicitacion: { label: "Felicitación" },
};

export const PQRS_CHANNELS = {
  app: { label: "App" },
  whatsapp: { label: "WhatsApp" },
  llamada: { label: "Llamada" },
  correo: { label: "Correo" },
  panel: { label: "Panel" },
};

export const PQRS_PRIORITIES = {
  alta: { label: "Alta", tone: "bad", dias: 2 },
  media: { label: "Media", tone: "warn", dias: 5 },
  baja: { label: "Baja", tone: "neutral", dias: 10 },
};

export const PQRS_STATES = {
  0: { label: "Abierto", tone: "warn" },
  1: { label: "En gestión", tone: "info" },
  2: { label: "Resuelto", tone: "ok" },
  3: { label: "Cerrado", tone: "neutral" },
};

/*
 * Solicitudes de la web pública.
 *
 * Los rótulos dicen qué pide la persona, no de qué formulario salió: quien
 * atiende la bandeja necesita saber si tiene que llamar a un tendero o borrar
 * una cuenta, no en qué página estaba el botón.
 */
export const SOLICITUD_TYPES = {
  comercio: { label: "Quiere vender" },
  domiciliario: { label: "Quiere repartir" },
  vecino: { label: "Vecino" },
  conjunto: { label: "Alta de conjunto" },
  cobertura: { label: "Pide cobertura" },
  eliminacion: { label: "Eliminar cuenta" },
  pqrs: { label: "PQRS" },
  otro: { label: "Sin clasificar" },
};

export const SOLICITUD_STATES = {
  0: { label: "Nueva", tone: "warn" },
  1: { label: "En gestión", tone: "info" },
  2: { label: "Atendida", tone: "ok" },
  3: { label: "Descartada", tone: "neutral" },
};

export const SETTLEMENT_TYPES = {
  business: { label: "Negocio" },
  domiciliary: { label: "Domiciliario" },
};

export const SETTLEMENT_STATES = {
  0: { label: "Borrador", tone: "neutral" },
  1: { label: "Aprobada", tone: "info" },
  2: { label: "Pagada", tone: "ok" },
  3: { label: "Anulada", tone: "bad" },
};

export const PUSH_STATES = {
  0: { label: "Borrador", tone: "neutral" },
  1: { label: "Programada", tone: "warn" },
  2: { label: "Enviada", tone: "ok" },
  3: { label: "Cancelada", tone: "bad" },
};
